/**
 * ReferralPage.jsx — Referral Pipeline Management
 *
 * Used by: frontdesk, clinician, supervisor, admin
 * Pipeline: new → contacted → scheduled → admitted → closed
 *
 * Features:
 *   - New referral entry
 *   - Pipeline view with status filters
 *   - Status change actions
 *   - Assign to staff
 *   - Link to intake when ready
 *
 * Powered by Cold Harbor Digital
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  createReferral, onOrgReferrals, updateReferralStatus, updateReferral,
  getTeamMembers, REFERRAL_SOURCES, PROGRAMS, INSURANCE_TYPES,
} from "../services/intakeService";
import {
  ArrowLeft, Plus, X, Phone, Calendar, UserPlus, CheckCircle,
  XCircle, Clock, Filter, ChevronRight, AlertTriangle, Search,
  ClipboardList, Users, FileText, ArrowRight, Edit3, PhoneCall,
} from "lucide-react";
import BottomNav from "../components/BottomNav";

// Pipeline status config
const STATUS_CONFIG = {
  new: { label: "New", color: "#63b3ed", bg: "rgba(99,179,237,0.1)", icon: Plus },
  contacted: { label: "Contacted", color: "#f6ad55", bg: "rgba(246,173,85,0.1)", icon: PhoneCall },
  scheduled: { label: "Scheduled", color: "#b794f4", bg: "rgba(183,148,244,0.1)", icon: Calendar },
  admitted: { label: "Admitted", color: "#68d391", bg: "rgba(104,211,145,0.1)", icon: CheckCircle },
  closed: { label: "Closed", color: "#718096", bg: "rgba(113,128,150,0.1)", icon: XCircle },
};

const URGENCY_CONFIG = {
  routine: { label: "Routine", color: "#68d391" },
  urgent: { label: "Urgent", color: "#ecc94b" },
  emergent: { label: "Emergent", color: "#fc8181" },
};

// Shared styles
const S = {
  label: { display: "block", fontSize: 11, fontWeight: 700, color: "#a0aec0", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 },
  input: {
    width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 500,
    background: "rgba(15,23,42,0.6)", color: "#e2e8f0",
    border: "1px solid rgba(99,179,237,0.1)", outline: "none", boxSizing: "border-box",
  },
  select: {
    width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 500,
    background: "rgba(15,23,42,0.6)", color: "#e2e8f0",
    border: "1px solid rgba(99,179,237,0.1)", outline: "none", appearance: "auto", boxSizing: "border-box",
  },
  textarea: {
    width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 500,
    background: "rgba(15,23,42,0.6)", color: "#e2e8f0",
    border: "1px solid rgba(99,179,237,0.1)", outline: "none",
    minHeight: 80, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box",
  },
  card: {
    background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)",
    borderRadius: 16, padding: 16, marginBottom: 12,
  },
  row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  fieldGroup: { marginBottom: 12 },
};

export default function ReferralPage() {
  const navigate = useNavigate();
  const { profile, role } = useAuth();
  const org = profile?.organization || localStorage.getItem("fieldnotes_user_org") || "default-org";

  const [referrals, setReferrals] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null); // referral object or null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // New referral form state
  const [newRef, setNewRef] = useState({
    firstName: "", lastName: "", dob: "", phone: "", email: "", address: "",
    sourceType: "", sourceName: "", sourcePhone: "", sourceEmail: "",
    reason: "", urgency: "routine", requestedProgram: "ICM",
    insuranceType: "", insuranceId: "", notes: "",
    assignedTo: "", assignedToName: "",
  });

  // Load referrals + team
  useEffect(() => {
    const unsub = onOrgReferrals(org, setReferrals);
    getTeamMembers(org).then(setTeamMembers);
    return unsub;
  }, [org]);

  // Filter & search
  const filtered = referrals.filter(r => {
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const name = `${r.firstName} ${r.lastName}`.toLowerCase();
      const source = (r.sourceName || "").toLowerCase();
      return name.includes(q) || source.includes(q);
    }
    return true;
  });

  // Pipeline counts
  const counts = {};
  Object.keys(STATUS_CONFIG).forEach(s => { counts[s] = referrals.filter(r => r.status === s).length; });

  const resetNewForm = () => setNewRef({
    firstName: "", lastName: "", dob: "", phone: "", email: "", address: "",
    sourceType: "", sourceName: "", sourcePhone: "", sourceEmail: "",
    reason: "", urgency: "routine", requestedProgram: "ICM",
    insuranceType: "", insuranceId: "", notes: "",
    assignedTo: "", assignedToName: "",
  });

  const handleCreateReferral = async () => {
    if (!newRef.firstName.trim() || !newRef.lastName.trim()) {
      setError("First and last name are required.");
      return;
    }
    setSaving(true);
    setError("");
    const result = await createReferral(newRef);
    if (result.success) {
      setShowNewForm(false);
      resetNewForm();
    } else {
      setError(result.error || "Failed to create referral.");
    }
    setSaving(false);
  };

  const handleStatusChange = async (referralId, newStatus, details = {}) => {
    setSaving(true);
    await updateReferralStatus(referralId, newStatus, details);
    setSaving(false);
    if (newStatus === "admitted") setShowDetail(null);
  };

  const handleStartIntake = (referral) => {
    navigate("/intake", { state: { referral } });
  };

  const formatDate = (d) => {
    if (!d) return "—";
    if (d.toDate) return d.toDate().toLocaleDateString();
    return new Date(d).toLocaleDateString();
  };

  const timeSince = (d) => {
    if (!d) return "";
    const date = d.toDate ? d.toDate() : new Date(d);
    const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return `${days}d ago`;
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0a0f1a", color: "#e2e8f0", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>

      {/* Header */}
      <header style={{
        padding: "16px 20px", background: "linear-gradient(135deg, #0f1729, #162033)",
        borderBottom: "1px solid rgba(246,173,85,0.15)", position: "sticky", top: 0, zIndex: 50,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "#a0aec0" }}>
              <ArrowLeft size={20} />
            </button>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#f6ad55" }}>Referrals</div>
              <div style={{ fontSize: 11, color: "#718096" }}>{referrals.length} total · {counts.new || 0} new</div>
            </div>
          </div>
          <button onClick={() => { setShowNewForm(true); setError(""); }} style={{
            padding: "8px 16px", borderRadius: 10, fontSize: 12, fontWeight: 700,
            background: "linear-gradient(135deg, #f6ad55, #ed8936)", color: "#1a202c",
            border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
          }}>
            <Plus size={14} /> New Referral
          </button>
        </div>
      </header>

      <div style={{ padding: "12px 20px", paddingBottom: 100 }}>

        {/* Pipeline Summary */}
        <div style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 16, paddingBottom: 4 }}>
          <button onClick={() => setFilterStatus("all")} style={{
            padding: "6px 14px", borderRadius: 10, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
            background: filterStatus === "all" ? "rgba(246,173,85,0.12)" : "transparent",
            color: filterStatus === "all" ? "#f6ad55" : "#718096",
            border: `1px solid ${filterStatus === "all" ? "rgba(246,173,85,0.2)" : "transparent"}`,
            cursor: "pointer",
          }}>
            All ({referrals.length})
          </button>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <button key={key} onClick={() => setFilterStatus(key)} style={{
              padding: "6px 14px", borderRadius: 10, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
              background: filterStatus === key ? cfg.bg : "transparent",
              color: filterStatus === key ? cfg.color : "#718096",
              border: `1px solid ${filterStatus === key ? `${cfg.color}33` : "transparent"}`,
              cursor: "pointer",
            }}>
              {cfg.label} ({counts[key] || 0})
            </button>
          ))}
        </div>

        {/* Search */}
        <div style={{ position: "relative", marginBottom: 16 }}>
          <Search size={14} style={{ position: "absolute", left: 12, top: 12, color: "#4a5568" }} />
          <input
            style={{ ...S.input, paddingLeft: 34 }}
            placeholder="Search by name or source..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Referral List */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0" }}>
            <ClipboardList size={32} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
            <div style={{ fontSize: 13, color: "#4a5568" }}>
              {filterStatus === "all" ? "No referrals yet" : `No ${STATUS_CONFIG[filterStatus]?.label.toLowerCase()} referrals`}
            </div>
          </div>
        ) : (
          filtered.map(ref => {
            const statusCfg = STATUS_CONFIG[ref.status] || STATUS_CONFIG.new;
            const urgencyCfg = URGENCY_CONFIG[ref.urgency] || URGENCY_CONFIG.routine;
            const StatusIcon = statusCfg.icon;

            return (
              <div key={ref.id} onClick={() => setShowDetail(ref)} style={{
                ...S.card, cursor: "pointer",
                borderLeft: `3px solid ${statusCfg.color}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#e2e8f0" }}>
                      {ref.firstName} {ref.lastName}
                    </div>
                    <div style={{ fontSize: 11, color: "#718096", marginTop: 2 }}>
                      {REFERRAL_SOURCES.find(s => s.value === ref.sourceType)?.label || ref.sourceType || "Unknown source"}
                      {ref.sourceName && ` — ${ref.sourceName}`}
                    </div>
                    {ref.reason && (
                      <div style={{ fontSize: 11, color: "#a0aec0", marginTop: 4, lineHeight: 1.4 }}>
                        {ref.reason.length > 80 ? ref.reason.slice(0, 80) + "..." : ref.reason}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 800,
                      background: statusCfg.bg, color: statusCfg.color, textTransform: "uppercase",
                    }}>
                      <StatusIcon size={10} /> {statusCfg.label}
                    </span>
                    {ref.urgency !== "routine" && (
                      <span style={{
                        padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 800,
                        background: `${urgencyCfg.color}15`, color: urgencyCfg.color, textTransform: "uppercase",
                      }}>
                        {urgencyCfg.label}
                      </span>
                    )}
                    <span style={{ fontSize: 10, color: "#4a5568" }}>{timeSince(ref.createdAt)}</span>
                  </div>
                </div>

                {/* Assignment + program row */}
                <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                  {ref.requestedProgram && (
                    <span style={{ fontSize: 10, color: "#63b3ed", fontWeight: 600 }}>
                      {PROGRAMS.find(p => p.value === ref.requestedProgram)?.label || ref.requestedProgram}
                    </span>
                  )}
                  {ref.assignedToName && (
                    <span style={{ fontSize: 10, color: "#b794f4", fontWeight: 600 }}>
                      → {ref.assignedToName}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ===== NEW REFERRAL MODAL ===== */}
      {showNewForm && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.8)", overflowY: "auto" }}
          onClick={() => setShowNewForm(false)}>
          <div style={{
            maxWidth: 480, margin: "40px auto", borderRadius: 20, overflow: "hidden",
            background: "linear-gradient(135deg, #0f1729, #162033)",
            border: "1px solid rgba(246,173,85,0.15)",
          }} onClick={e => e.stopPropagation()}>

            {/* Modal Header */}
            <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(99,179,237,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#f6ad55" }}>New Referral</div>
              <button onClick={() => setShowNewForm(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#718096" }}><X size={20} /></button>
            </div>

            <div style={{ padding: 20 }}>
              {error && (
                <div style={{ padding: "8px 12px", borderRadius: 10, background: "rgba(252,129,129,0.06)", border: "1px solid rgba(252,129,129,0.15)", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={14} style={{ color: "#fc8181" }} />
                  <span style={{ fontSize: 12, color: "#fc8181" }}>{error}</span>
                </div>
              )}

              {/* Individual Info */}
              <div style={{ fontSize: 11, fontWeight: 800, color: "#63b3ed", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Individual</div>
              <div style={S.row}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>First Name *</label>
                  <input style={S.input} value={newRef.firstName} onChange={e => setNewRef(p => ({ ...p, firstName: e.target.value }))} />
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Last Name *</label>
                  <input style={S.input} value={newRef.lastName} onChange={e => setNewRef(p => ({ ...p, lastName: e.target.value }))} />
                </div>
              </div>
              <div style={S.row}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>DOB</label>
                  <input type="date" style={S.input} value={newRef.dob} onChange={e => setNewRef(p => ({ ...p, dob: e.target.value }))} />
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Phone</label>
                  <input type="tel" style={S.input} value={newRef.phone} onChange={e => setNewRef(p => ({ ...p, phone: e.target.value }))} />
                </div>
              </div>

              {/* Referral Source */}
              <div style={{ fontSize: 11, fontWeight: 800, color: "#63b3ed", marginBottom: 8, marginTop: 16, textTransform: "uppercase", letterSpacing: 1 }}>Referral Source</div>
              <div style={S.row}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Source Type</label>
                  <select style={S.select} value={newRef.sourceType} onChange={e => setNewRef(p => ({ ...p, sourceType: e.target.value }))}>
                    <option value="">Select...</option>
                    {REFERRAL_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Source Name</label>
                  <input style={S.input} value={newRef.sourceName} onChange={e => setNewRef(p => ({ ...p, sourceName: e.target.value }))} placeholder="Person or organization" />
                </div>
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>Source Phone</label>
                <input type="tel" style={S.input} value={newRef.sourcePhone} onChange={e => setNewRef(p => ({ ...p, sourcePhone: e.target.value }))} />
              </div>

              {/* Details */}
              <div style={{ fontSize: 11, fontWeight: 800, color: "#63b3ed", marginBottom: 8, marginTop: 16, textTransform: "uppercase", letterSpacing: 1 }}>Details</div>
              <div style={S.fieldGroup}>
                <label style={S.label}>Reason for Referral</label>
                <textarea style={S.textarea} value={newRef.reason} onChange={e => setNewRef(p => ({ ...p, reason: e.target.value }))} placeholder="Brief description..." />
              </div>
              <div style={S.row}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Urgency</label>
                  <select style={S.select} value={newRef.urgency} onChange={e => setNewRef(p => ({ ...p, urgency: e.target.value }))}>
                    <option value="routine">Routine</option>
                    <option value="urgent">Urgent</option>
                    <option value="emergent">Emergent</option>
                  </select>
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Requested Program</label>
                  <select style={S.select} value={newRef.requestedProgram} onChange={e => setNewRef(p => ({ ...p, requestedProgram: e.target.value }))}>
                    {PROGRAMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={S.row}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Insurance Type</label>
                  <select style={S.select} value={newRef.insuranceType} onChange={e => setNewRef(p => ({ ...p, insuranceType: e.target.value }))}>
                    <option value="">Select...</option>
                    {INSURANCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Insurance ID</label>
                  <input style={S.input} value={newRef.insuranceId} onChange={e => setNewRef(p => ({ ...p, insuranceId: e.target.value }))} />
                </div>
              </div>

              {/* Assignment */}
              <div style={S.fieldGroup}>
                <label style={S.label}>Assign To (Intake Coordinator)</label>
                <select style={S.select} value={newRef.assignedTo} onChange={e => {
                  const member = teamMembers.find(m => m.id === e.target.value);
                  setNewRef(p => ({ ...p, assignedTo: e.target.value, assignedToName: member?.displayName || "" }));
                }}>
                  <option value="">Unassigned</option>
                  {teamMembers.filter(m => ["frontdesk", "supervisor", "admin"].includes(m.role)).map(m => (
                    <option key={m.id} value={m.id}>{m.displayName} ({m.role})</option>
                  ))}
                </select>
              </div>

              <div style={S.fieldGroup}>
                <label style={S.label}>Notes</label>
                <textarea style={S.textarea} value={newRef.notes} onChange={e => setNewRef(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes..." />
              </div>

              {/* Submit */}
              <button onClick={handleCreateReferral} disabled={saving} style={{
                width: "100%", padding: "14px 0", borderRadius: 14, fontSize: 14, fontWeight: 800, marginTop: 8,
                background: saving ? "rgba(246,173,85,0.3)" : "linear-gradient(135deg, #f6ad55, #ed8936)",
                color: "#1a202c", border: "none", cursor: saving ? "default" : "pointer",
              }}>
                {saving ? "Creating..." : "Create Referral"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== REFERRAL DETAIL MODAL ===== */}
      {showDetail && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.8)", overflowY: "auto" }}
          onClick={() => setShowDetail(null)}>
          <div style={{
            maxWidth: 480, margin: "40px auto", borderRadius: 20, overflow: "hidden",
            background: "linear-gradient(135deg, #0f1729, #162033)",
            border: `1px solid ${STATUS_CONFIG[showDetail.status]?.color || "#63b3ed"}33`,
          }} onClick={e => e.stopPropagation()}>

            {/* Detail Header */}
            <div style={{
              padding: "16px 20px", borderBottom: "1px solid rgba(99,179,237,0.08)",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#e2e8f0" }}>
                  {showDetail.firstName} {showDetail.lastName}
                </div>
                <div style={{ fontSize: 11, color: "#718096", marginTop: 2 }}>
                  Referral · {formatDate(showDetail.createdAt)}
                </div>
              </div>
              <button onClick={() => setShowDetail(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#718096" }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: 20 }}>
              {/* Status Badge */}
              {(() => {
                const cfg = STATUS_CONFIG[showDetail.status];
                const Icon = cfg.icon;
                return (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 20, background: cfg.bg, marginBottom: 16 }}>
                    <Icon size={12} style={{ color: cfg.color }} />
                    <span style={{ fontSize: 12, fontWeight: 800, color: cfg.color, textTransform: "uppercase" }}>{cfg.label}</span>
                  </div>
                );
              })()}

              {/* Info Cards */}
              <div style={S.card}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Individual</div>
                {showDetail.dob && <div style={{ fontSize: 12, color: "#a0aec0" }}>DOB: {showDetail.dob}</div>}
                {showDetail.phone && <div style={{ fontSize: 12, color: "#a0aec0" }}>Phone: {showDetail.phone}</div>}
                {showDetail.email && <div style={{ fontSize: 12, color: "#a0aec0" }}>Email: {showDetail.email}</div>}
                {showDetail.address && <div style={{ fontSize: 12, color: "#a0aec0" }}>Address: {showDetail.address}</div>}
              </div>

              <div style={S.card}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Source</div>
                <div style={{ fontSize: 12, color: "#a0aec0" }}>
                  {REFERRAL_SOURCES.find(s => s.value === showDetail.sourceType)?.label || showDetail.sourceType || "—"}
                  {showDetail.sourceName && ` — ${showDetail.sourceName}`}
                </div>
                {showDetail.sourcePhone && <div style={{ fontSize: 12, color: "#a0aec0" }}>Phone: {showDetail.sourcePhone}</div>}
              </div>

              {showDetail.reason && (
                <div style={S.card}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Reason</div>
                  <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6 }}>{showDetail.reason}</div>
                </div>
              )}

              <div style={S.card}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Details</div>
                <div style={{ fontSize: 12, color: "#a0aec0" }}>
                  Urgency: <span style={{ color: URGENCY_CONFIG[showDetail.urgency]?.color, fontWeight: 700 }}>{showDetail.urgency}</span>
                </div>
                <div style={{ fontSize: 12, color: "#a0aec0" }}>
                  Program: {PROGRAMS.find(p => p.value === showDetail.requestedProgram)?.label || showDetail.requestedProgram || "—"}
                </div>
                {showDetail.insuranceType && (
                  <div style={{ fontSize: 12, color: "#a0aec0" }}>
                    Insurance: {INSURANCE_TYPES.find(t => t.value === showDetail.insuranceType)?.label || showDetail.insuranceType}
                    {showDetail.insuranceId && ` (${showDetail.insuranceId})`}
                  </div>
                )}
                {showDetail.assignedToName && (
                  <div style={{ fontSize: 12, color: "#a0aec0" }}>Assigned: {showDetail.assignedToName}</div>
                )}
              </div>

              {/* Timeline */}
              <div style={S.card}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Timeline</div>
                {[
                  { label: "Referred", date: showDetail.referralDate || showDetail.createdAt, done: true },
                  { label: "Contacted", date: showDetail.contactedDate, done: !!showDetail.contactedDate },
                  { label: "Scheduled", date: showDetail.scheduledDate, done: !!showDetail.scheduledDate },
                  { label: "Admitted", date: showDetail.admittedDate, done: !!showDetail.admittedDate },
                ].map((step, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                      background: step.done ? "rgba(104,211,145,0.15)" : "rgba(45,55,72,0.5)",
                    }}>
                      {step.done && <Check size={10} style={{ color: "#68d391" }} />}
                    </div>
                    <span style={{ fontSize: 12, color: step.done ? "#68d391" : "#4a5568", fontWeight: step.done ? 700 : 400 }}>
                      {step.label}
                    </span>
                    {step.date && <span style={{ fontSize: 10, color: "#4a5568", marginLeft: "auto" }}>{formatDate(step.date)}</span>}
                  </div>
                ))}
              </div>

              {showDetail.notes && (
                <div style={S.card}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Notes</div>
                  <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6 }}>{showDetail.notes}</div>
                </div>
              )}

              {/* Action Buttons */}
              {showDetail.status !== "admitted" && showDetail.status !== "closed" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                  {showDetail.status === "new" && (
                    <button onClick={() => handleStatusChange(showDetail.id, "contacted")} disabled={saving} style={{
                      width: "100%", padding: "12px 0", borderRadius: 12, fontSize: 13, fontWeight: 700,
                      background: "rgba(246,173,85,0.1)", color: "#f6ad55",
                      border: "1px solid rgba(246,173,85,0.2)", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}>
                      <PhoneCall size={14} /> Mark as Contacted
                    </button>
                  )}

                  {(showDetail.status === "new" || showDetail.status === "contacted") && (
                    <button onClick={() => handleStatusChange(showDetail.id, "scheduled")} disabled={saving} style={{
                      width: "100%", padding: "12px 0", borderRadius: 12, fontSize: 13, fontWeight: 700,
                      background: "rgba(183,148,244,0.1)", color: "#b794f4",
                      border: "1px solid rgba(183,148,244,0.2)", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}>
                      <Calendar size={14} /> Schedule Intake
                    </button>
                  )}

                  {(showDetail.status === "scheduled" || showDetail.status === "contacted") && (
                    <button onClick={() => handleStartIntake(showDetail)} style={{
                      width: "100%", padding: "14px 0", borderRadius: 12, fontSize: 14, fontWeight: 800,
                      background: "linear-gradient(135deg, #48bb78, #38a169)", color: "white",
                      border: "none", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}>
                      <UserPlus size={16} /> Start Intake
                    </button>
                  )}

                  <button onClick={() => handleStatusChange(showDetail.id, "closed", { closedReason: "Declined / No-show" })} disabled={saving} style={{
                    width: "100%", padding: "10px 0", borderRadius: 12, fontSize: 12, fontWeight: 600,
                    background: "transparent", color: "#718096",
                    border: "1px solid rgba(99,179,237,0.08)", cursor: "pointer",
                  }}>
                    Close Referral
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <BottomNav active="referrals" />
    </div>
  );
}